<template>
  <Listbox
    v-model="selected_item"
    as="div"
    :class="[classes]"
    class="!border-none bg-light-box-background dark:bg-dark-box-background p-0 relative">
    <ListboxLabel
      v-if="label"
      class="block text-base dark:text-dark-body-text bg-light-box-background dark:bg-dark-box-background font-medium text-gray-700"
      :for="name">
      {{ label }}
    </ListboxLabel>
    <ListboxButton as="template">
      <div class="mt-1 flex shadow-sm">
        <div class="relative flex items-stretch flex-grow focus-within:z-10">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search class="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>

          <input
            :value="search_term"
            :disabled="disabled"
            autocomplete="off"
            :placeholder="translatedPlaceholder"
            :class="[
              search_term?.length > 0 ? 'rounded-br-none rounded-tr-none' : '',
              errorMessage
                ? 'border-status-error-red text-status-error-red placeholder-status-error-red focus:outline-none focus:ring-status-error-red focus:border-status-error-red'
                : '',
              disabled ? 'text-common-grey' : '',
              inputClasses,
              isBoxBackground
                ? 'bg-light-box-background dark:bg-dark-box-background'
                : 'bg-light-screen-background dark:bg-dark-screen-background',

              'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light '
            ]"
            class="focus:ring-blue focus:border-blue-light block w-full placeholder-common-grey border rounded-lg pl-10 border-light-divider-border dark:border-dark-divider-border transition ease-in-out duration-side-menu-transition-duration text-light-body-text dark:text-dark-body-text"
            @blur="handleChange"
            @click="search"
            @keydown="
              ($event) => {
                if ($event.keyCode == 32) {
                  $event.stopPropagation()
                }
              }
            "
            @input="
              ($event) => {
                show_results = true
                ;(search_term = $event.target.value), handleChange($event)
              }
            ">
          <div v-show="errorMessage" class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <svg class="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
              <path stroke-linecap="round" stroke-width="1.5" d="M1 1l6 6m0-6L1 7" />
            </svg>
          </div>
          <p v-if="errorMessage && !meta.valid" class="absolute inset-y-0 top-10 right-0 leading-tight pt-[2px] text-[13px] text-red-400">
            {{ errorMessage }}
          </p>
          <p v-else-if="infoMessage" class="absolute inset-y-0 top-10 left-0 leading-tight pt-[2px] text-[13px] text-gray-400">
            {{ infoMessage }}
          </p>
        </div>
        <button
          v-if="search_term?.length > 0 && !disabled && !is_searching && add"
          type="button"
          :class="
            isBoxBackground
              ? 'bg-light-box-background dark:bg-dark-box-background'
              : 'bg-light-screen-background dark:bg-dark-screen-background'
          "
          class="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border rounded-bl-none rounded-tl-none border-light-divider-border dark:border-dark-divider-border text-sm font-medium rounded-r-md focus:outline-none transition ease-in-out duration-side-menu-transition-duration"
          @click="addClick">
          <div>{{ addLabel }}</div>
        </button>
        <button
          v-if="search_results.length && !disabled && !is_searching"
          type="button"
          :class="
            isBoxBackground
              ? 'bg-light-box-background dark:bg-dark-box-background'
              : 'bg-light-screen-background dark:bg-dark-screen-background'
          "
          class="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border rounded-bl-none rounded-tl-none border-light-divider-border dark:border-dark-divider-border text-sm font-medium rounded-r-md focus:outline-none transition ease-in-out duration-side-menu-transition-duration"
          @click="reset">
          <X class="h-5 w-5 text-gray-400 hover:text-gray-700" aria-hidden="true" />
        </button>
        <button
          v-else-if="is_searching"
          type="button"
          :class="
            isBoxBackground
              ? 'bg-light-box-background dark:bg-dark-box-background'
              : 'bg-light-screen-background dark:bg-dark-screen-background'
          "
          class="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border rounded-bl-none rounded-tl-none text-sm font-medium rounded-r-md focus:outline-none">
          <RotateCw class="animate-spin h-5 w-5 text-gray-500" aria-hidden="true" />
        </button>
      </div>
    </ListboxButton>

    <transition
      v-show="search_results.length > 0 && !is_searching"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0">
      <ListboxOptions
        static
        :class="
          isBoxBackground
            ? 'bg-light-box-background dark:bg-dark-box-background'
            : 'bg-light-screen-background dark:bg-dark-screen-background'
        "
        class="absolute inset-0 top-[42px] z-10 w-full bg-white shadow-lg max-h-60 rounded-md text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
        style="min-height: fit-content">
        <ListboxOption v-for="item in search_results" v-slot="{}" :key="item" :value="item">
          <div
            :class="''"
            class="group cursor-pointer hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue relative py-2 pl-8 pr-4 rounded-lg transition ease-in-out duration-side-menu-transition-duration">
            <span
              :class="['text-light-body-text dark:text-dark-body-text group-hover:text-dark-body-text']"
              class="block truncate transition ease-in-out duration-side-menu-transition-duration">
              {{ item[labelAttr] }}
            </span>
            <span
              :class="['text-light-body-text dark:text-dark-body-text group-hover:text-dark-body-text']"
              class="absolute inset-y-0 left-0 flex items-center pl-1.5 transition ease-in-out duration-side-menu-transition-duration">
              <Plus class="h-5 w-5" aria-hidden="true" />
            </span>
          </div>
        </ListboxOption>
      </ListboxOptions>
    </transition>
  </Listbox>
</template>

<script>
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from "@headlessui/vue"
import { Search, X, Plus, RotateCw } from "lucide-vue-next"
import dataService from "@/services/data.service"
import { useField } from "vee-validate"

export default {
  components: {
    Search,
    X,
    RotateCw,
    Plus,
    Listbox,
    ListboxLabel,
    ListboxOptions,
    ListboxOption,
    ListboxButton
  },
  props: {
    selectedItems: {
      type: Array,
      required: false
    },

    addLabel: {
      type: String,
      required: false
    },
    add: {
      type: Boolean,
      default: false
    },
    clearOnSelect: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      required: true
    },
    value: {
      type: String,
      required: false
    },
    label: {
      type: String,
      required: false
    },
    infoMessage: {
      type: String,
      default: ""
    },
    placeholder: {
      type: String,
      required: false,
      default: "Search"
    },
    inputClasses: {
      type: String,
      default: "text"
    },
    classes: {
      type: String,
      default: "text"
    },
    disabled: {
      type: Boolean,
      default: false
    },
    endpoint: {
      type: String,
      required: true
    },
    labelAttr: {
      type: String,
      default: "name"
    },
    valueAttr: {
      type: String,
      required: false
    },
    searchField: {
      type: String,
      default: "search"
    },
    filterField: {
      type: String,
      required: false
    },
    filterFieldValue: {
      type: String,
      required: false
    },
    endpointMethod: {
      type: String,
      default: "get"
    },
    isBoxBackground: {
      type: Boolean,
      default: false
    },
    debounce: {
      type: Number,
      default: 0
    }
  },
  emits: ["chosen", "submitted", "changed"],
  setup(props) {
    const {
      errorMessage,

      handleChange,
      setValue,
      meta
    } = useField(props.name, undefined, {
      initialValue: props.value
    })

    return {
      handleChange,

      setValue,
      errorMessage,

      meta
    }
  },

  data() {
    return {
      is_searching: false,
      show_results: true,
      search_term: null,
      search_results: [],
      selected_item: null,
      dont_fetch: false,
      change_timeout: null
    }
  },
  computed: {
    translatedPlaceholder() {
      return this.$t(this.placeholder)
    }
  },
  watch: {
    search_term() {
      this.$emit("changed", this.search_term)
      if (this.search_term != null && this.show_results) {
        if (!this.dont_fetch) {
          clearTimeout(this.change_timeout)
          this.change_timeout = setTimeout(() => {
            this.search()
          }, this.debounce)
        }
      }
    },
    selected_item() {
      if (this.selected_item) {
        this.$emit("chosen", { ...this.selected_item })
        if (this.clearOnSelect) {
          this.search_term = ""
        } else {
          this.search_term = this.selected_item[this.labelAttr]
        }
        this.search_results = []
        this.show_results = false
        if (this.valueAttr) {
          this.setValue(this.selected_item[this.valueAttr])
        }
      }
    }
  },

  methods: {
    search() {
      this.is_searching = true
      let filter = {}
      if (this.search_term == null) {
        this.dont_fetch = true
        setTimeout(() => {
          this.dont_fetch = false
        }, 500)
        this.search_term = ""
      }
      filter[this.searchField] = this.search_term
      if (this.filterField && this.filterFieldValue) {
        filter[this.filterField] = this.filterFieldValue
      }

      if (this.endpointMethod == "get") {
        dataService
          .get(this.endpoint, filter)
          .then((result) => {
            this.is_searching = false
            this.search_results = result.data.results

            this.search_results = this.search_results.filter(
              (item) => !this.selectedItems.find((s_item) => (s_item.id ? s_item.id == item.id : s_item == item.id))
            )
          })
          .catch(() => {
            this.is_searching = false
          })
      } else {
        dataService
          .post(this.endpoint, filter)
          .then((result) => {
            this.is_searching = false
            this.search_results = result.data.results
            this.search_results = this.search_results.filter(
              (item) => !this.selectedItems.find((s_item) => (s_item.id ? s_item.id == item.id : s_item == item.id))
            )
          })
          .catch(() => {
            this.is_searching = false
          })
      }
    },
    reset() {
      this.search_term = null
      this.search_results = []
      this.show_results = false
    },

    addClick() {
      this.$emit("submitted", this.search_term)
      this.search_term = ""
      this.search_results = []
      this.show_results = false
    }
  }
}
</script>

<style scoped>
.mh-48 {
  max-height: 10rem;
}
</style>
